import FormTypeStep from "./steps/FormTypeStep.jsx";
import AmendedReturnStep from "./steps/AmendedReturnStep.jsx";
import WeightIncreaseStep from "./steps/WeightIncreaseStep.jsx";
import VinCorrectionStep from "./steps/VinCorrectionStep.jsx";
import TrucksStep from "./steps/TrucksStep.jsx";
import PriorYearStep from "./steps/PriorYearStep.jsx";
import CreditsStep from "./steps/CreditsStep.jsx";
import ReviewStep from "./steps/ReviewStep.jsx";
import PayStep from "./steps/PayStep.jsx";
import PaymentDetailsStep from "./steps/PaymentDetailsStep.jsx";
import { money } from "../../utils/format.js";

// Single source of truth for the wizard's step sequence - both Wizard.jsx
// (routing) and Stepper.jsx (sidebar) read from this, so they can never
// drift out of sync.
//
// The sequence is built fresh from state on every render, so it grows and
// shrinks with the filing type: Original/Final returns keep the plain
// Business -> Form -> Trucks -> ... sequence, while Amended and VIN
// Correction returns insert their own dedicated page(s) - the IRS's own
// AmendedReturnInd/VINCorrectionInd (IRS2290.xsd) are separate, mutually
// exclusive indicators, so each gets its own screen instead of expanding
// inline on the Form step. "Trucks" (and everything after it) simply shifts
// to a later position for those filers; nothing after Form is skipped.
//
// Tax year isn't a separate step - each filing form (Trucks/Original, VIN
// Correction, Amended's mileage-reason block) collects it inline, right
// above that form's own Month first used field.
//
// isDone/subFor are called as (state, totals, position) where position is
// the step's 1-based index in the CURRENT sequence - this is what
// state.maxStep is compared against, so "have I passed this step" stays
// correct even as steps are inserted/removed above it.
export function buildSteps(state) {
  const steps = [];

  // Tax Preparer and Business are no longer wizard steps - both are
  // collected up front from their own dashboard pages (Businesses.jsx /
  // TaxPreparers.jsx, via BusinessForm.jsx / TaxPreparerForm.jsx reusing
  // these same step components standalone) before a filing ever enters the
  // wizard, so the wizard now starts at Form with state.businessId already set.

  steps.push({
    key: "form",
    label: "Form",
    component: FormTypeStep,
    hideTicketPanel: true,
    // "New Filing" (sidebar/Dashboard) lands directly on this step with
    // state.returnType still blank - buildSteps() still returns a full
    // sequence in that case (the same shape as Original, minus Prior
    // year/Credits, since neither "amended"/"vincorrection" nor "original"
    // matches yet), so the stepper shows right away instead of waiting for a
    // filing type to be picked. It updates live the moment the filer picks
    // one (Amended/VIN Correction insert their own step(s) and drop Trucks;
    // Original/Final add Prior year/Credits) - no separate hidden state to
    // reconcile.
    isDone: (s, t, pos) => s.maxStep > pos,
    subFor: (s, t, pos) => (s.maxStep > pos ? "Complete" : "Not started"),
  });

  if (state.returnType === "amended") {
    steps.push({
      key: "amendedReturn",
      label: "Amended return",
      component: AmendedReturnStep,
      hideTicketPanel: true,
      isDone: (s, t, pos) => s.maxStep > pos,
      subFor: (s) => (s.amendReason ? "Complete" : "Not started"),
    });
    // Mileage-exceeded is collected inline on the Amended return page above;
    // only weight increase (its own TGWIncreaseWorksheet) needs a second page.
    if (state.amendReason === "weight") {
      steps.push({
        key: "amendDetail",
        label: "Weight increase",
        component: WeightIncreaseStep,
        hideTicketPanel: true,
        isDone: (s, t, pos) => s.maxStep > pos,
        subFor: (s, t, pos) => (s.maxStep > pos ? "Complete" : "Not started"),
      });
    }
  } else if (state.returnType === "vincorrection") {
    steps.push({
      key: "vinCorrection",
      label: "VIN correction",
      component: VinCorrectionStep,
      hideTicketPanel: true,
      isDone: (s, t, pos) => s.maxStep > pos,
      subFor: (s, t, pos) => (s.maxStep > pos ? "Complete" : "Not started"),
    });
  }

  // Amended and VIN Correction filers enter their vehicles right on their
  // own dedicated page (the TGW worksheet / mileage-exceeded vehicle list /
  // corrected-VIN list) instead of a separate Trucks step. Final returns DO
  // use this same Trucks step (F2290-027-01 only requires a VIN when the
  // Final checkbox is NOT checked - a Final filer may still list vehicles
  // and owe tax), it's just optional for them - see TrucksStep's
  // handleContinue.
  const isFinalReturn = !!state.finalReturn || state.returnType === "final";

  if (state.returnType !== "amended" && state.returnType !== "vincorrection") {
    steps.push({
      key: "trucks",
      label: "Trucks",
      component: TrucksStep,
      isDone: (s) => s.trucks.length > 0,
      subFor: (s) => (s.trucks.length ? `${s.trucks.length} truck${s.trucks.length > 1 ? "s" : ""}` : "Not started"),
    });
  }

  // Prior Year (Part II, Lines 8a/8b/9) and Credits (Line 5) are plain
  // siblings of FinalReturnInd in IRS2290.xsd - no schema rule or business
  // rule ties either to filing type, so a Final filer gets the same two
  // steps as an Original filer. Only Amended/VIN Correction skip them: those
  // have their own dedicated page(s) above instead (see the block starting
  // at "if (state.returnType === 'amended')").
  if (state.returnType === "original" || isFinalReturn) {
    steps.push({
      key: "priorYear",
      label: "Prior year",
      component: PriorYearStep,
      isDone: (s, t, pos) => s.maxStep > pos,
      subFor: (s, t, pos) => (s.maxStep > pos ? "Complete" : "—"),
    });

    steps.push({
      key: "credits",
      label: "Credits",
      component: CreditsStep,
      isDone: (s, t, pos) => s.maxStep > pos,
      subFor: (s, t, pos) => (s.maxStep > pos ? "Complete" : "—"),
    });
  }

  // Tax Summary used to be its own page here, but it only ever repeated the
  // vehicle list and balance due that Review already shows right below it -
  // folded into Review (see ReviewStep's "Tax summary" section) instead of
  // making filers click through two near-duplicate pages.
  steps.push({
    key: "review",
    label: "Review",
    component: ReviewStep,
    isDone: (s, t, pos) => s.maxStep > pos,
    subFor: (s, t, pos) => (s.maxStep > pos ? `${money(t.line6)} due` : "—"),
  });

  steps.push({
    key: "pay",
    label: "Payment method",
    component: PayStep,
    isDone: (s) => !!s.pay.method,
    subFor: (s) => (s.pay.method ? "Method chosen" : "—"),
  });

  // Payment details is the wizard's last step - completing it files the
  // return directly (see PaymentDetailsStep's fileReturn), rather than
  // advancing to another page.
  steps.push({
    key: "paymentDetails",
    label: "Payment details",
    component: PaymentDetailsStep,
    isDone: (s) => s.serviceFee.status === "SUCCESS",
    subFor: (s) => (s.serviceFee.status === "SUCCESS" ? "Paid" : "—"),
  });

  return steps;
}
